/**
 * Centralized File Upload Configuration
 * 
 * This file contains all standardized file type configurations used across the application
 * for file uploads, drag & drop, and file validation.
 */

// Maximum file size for images (2 MB)
export const MAX_IMAGE_SIZE = 2048 * 1024;

// Maximum filename length — must match MAX_ORIGINAL_FILENAME_LENGTH in backend/routers/files.py
// and MAX_FILENAME_LENGTH in backend/services/cab/cab_service.py
export const MAX_FILENAME_LENGTH = 200;

// Image file extensions
export const IMAGE_EXTENSIONS = [
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.svg'
] as const;

// Document file extensions
export const DOCUMENT_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.ppt',
  '.pptx',
  '.txt',
  '.md',
  '.json',
  '.csv',
  '.xls',
  '.xlsx',
  '.html'
] as const;

// All supported file extensions (derived from IMAGE_EXTENSIONS + DOCUMENT_EXTENSIONS)
export const SUPPORTED_FILE_EXTENSIONS = [
  ...IMAGE_EXTENSIONS,
  ...DOCUMENT_EXTENSIONS
] as const;

// MIME types for images
export const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml'
] as const;

// MIME types for documents
export const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/vnd.ms-powerpoint', // .ppt
  'text/plain', // .txt
  'text/markdown', // .md
  'application/json', // .json
  'text/csv', // .csv
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'text/html' // .html
] as const;

// All supported MIME types
export const ALL_SUPPORTED_MIME_TYPES = [
  ...IMAGE_MIME_TYPES,
  ...DOCUMENT_MIME_TYPES
] as const;

// Accept attribute string for file inputs (all formats)
export const FILE_INPUT_ACCEPT = SUPPORTED_FILE_EXTENSIONS.join(',');

// Accept attribute string for file inputs (images only)
export const IMAGE_INPUT_ACCEPT = IMAGE_EXTENSIONS.join(',');

// Accept attribute string for file inputs (documents only)
export const DOCUMENT_INPUT_ACCEPT = DOCUMENT_EXTENSIONS.join(',');

// Human-readable format lists for error messages
export const SUPPORTED_IMAGE_FORMATS = 'PNG, JPG, JPEG, GIF, WEBP, SVG';
export const SUPPORTED_DOCUMENT_FORMATS = 'PDF, DOCX, DOC, PPTX, PPT, TXT, MD, JSON, CSV, XLS, XLSX, HTML';
export const SUPPORTED_ALL_FORMATS = `${SUPPORTED_IMAGE_FORMATS}, ${SUPPORTED_DOCUMENT_FORMATS}`;

/**
 * Check if a file is an image based on MIME type
 */
export const isImageFile = (file: File): boolean => {
  return IMAGE_MIME_TYPES.includes(file.type as any);
};

/**
 * Check if a file is a document based on MIME type
 */
export const isDocumentFile = (file: File): boolean => {
  return DOCUMENT_MIME_TYPES.includes(file.type as any);
};

/**
 * Check if a file extension is supported
 */
export const isSupportedExtension = (filename: string): boolean => {
  const extension = filename.toLowerCase().match(/\.[^.]+$/)?.[0];
  return extension ? SUPPORTED_FILE_EXTENSIONS.includes(extension as any) : false;
};

/**
 * Check if a file is an image based on extension
 */
export const isImageExtension = (filename: string): boolean => {
  const extension = filename.toLowerCase().match(/\.[^.]+$/)?.[0];
  return extension ? IMAGE_EXTENSIONS.includes(extension as any) : false;
};

/**
 * Check if a file is a document based on extension
 */
export const isDocumentExtension = (filename: string): boolean => {
  const extension = filename.toLowerCase().match(/\.[^.]+$/)?.[0];
  return extension ? DOCUMENT_EXTENSIONS.includes(extension as any) : false;
};

/**
 * Validate if a file is supported (checks both MIME type and extension)
 */
export const isFileSupported = (file: File): boolean => {
  const mimeTypeSupported = ALL_SUPPORTED_MIME_TYPES.includes(file.type as any);
  const extensionSupported = isSupportedExtension(file.name);
  return mimeTypeSupported || extensionSupported;
};

/**
 * Validate image file size
 */
export const isImageSizeValid = (file: File): boolean => {
  return file.size <= MAX_IMAGE_SIZE;
};

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename: string): string => {
  const match = filename.toLowerCase().match(/\.[^.]+$/);
  return match ? match[0] : '';
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i)) + ' ' + sizes[i];
};

// ─── CAB-specific allowed extensions (matches ALL_ALLOWED_EXTENSIONS in cab_models.py) ───
export const CAB_ALLOWED_EXTENSIONS = new Set([
  // Documents
  '.pdf', '.docx', '.doc', '.pptx', '.ppt', '.xlsx', '.xls', '.csv', '.md', '.txt', '.html',
  // Source code
  '.py', '.js', '.ts', '.jsx', '.tsx', '.java', '.cs', '.cpp', '.c', '.go', '.rs', '.rb',
  '.php', '.swift', '.kt', '.json', '.xml', '.yaml', '.yml', '.sql', '.css', '.scss',
  '.vue', '.svelte',
  // Archives
  '.zip', '.tar', '.gz',
  // Images
  '.png', '.jpg', '.jpeg', '.gif',
]);

/**
 * Validate files before uploading to the general files/images endpoints.
 * Mirrors the backend checks in routers/files.py:
 *   1. Extension must be in SUPPORTED_FILE_EXTENSIONS (is_allowed_file)
 *   2. Filename must be <= MAX_FILENAME_LENGTH
 * Returns an array of error strings (one per rejected file).
 * Call BEFORE issuing any network requests to give instant feedback.
 */
export const validateFilesForUpload = (files: File[]): string[] => {
  const errors: string[] = [];
  for (const file of files) {
    const ext = (file.name.match(/\.[^.]+$/) ?? [''])[0].toLowerCase();
    if (!SUPPORTED_FILE_EXTENSIONS.includes(ext as any)) {
      errors.push(
        `"${file.name}" — file type "${ext || '(none)'}" is not supported. Allowed: ${SUPPORTED_ALL_FORMATS}.`
      );
    } else if (file.name.length > MAX_FILENAME_LENGTH) {
      errors.push(
        `"${file.name.slice(0, 40)}…" — filename is too long (${file.name.length} chars). Maximum is ${MAX_FILENAME_LENGTH} characters.`
      );
    }
  }
  return errors;
};

/**
 * Validate files before uploading to the CAB endpoint.
 * Checks both extension and filename length, mirroring cab_service.py.
 * Returns an array of error strings (one per rejected file).
 */
export const validateCABFilesForUpload = (files: File[]): string[] => {
  const errors: string[] = [];
  for (const file of files) {
    const ext = (file.name.match(/\.[^.]+$/) ?? [''])[0].toLowerCase();
    if (!CAB_ALLOWED_EXTENSIONS.has(ext)) {
      errors.push(
        `"${file.name}" — file type "${ext || '(none)'}" is not supported for CAB upload.`
      );
    } else if (file.name.length > MAX_FILENAME_LENGTH) {
      errors.push(
        `"${file.name.slice(0, 40)}…" — filename is too long (${file.name.length} chars). Maximum is ${MAX_FILENAME_LENGTH} characters.`
      );
    }
  }
  return errors;
};
